import React, { useState } from "react";
import { Alert, Box, Button, CircularProgress, Dialog, DialogContent, Divider, IconButton, InputAdornment, Popover, TextField, Typography, } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import { useTranslation } from "react-i18next";
import { useLoginMutation } from "../../app/store/api/authApi";
import { setCredentials } from "../../app/store/authSlice";
import { useDispatch } from "react-redux";
import NewPasswordDialog from "./NewPasswordDialog";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import { decodeIdToken } from "../utils/decodeIdToken";
import { getRouteForUser } from "../utils/roleRoutes";

const LoginPopup = ({ viewType = "desktop", openPopup, onClosePopup }) => {
    const {t, i18n} = useTranslation();
    const [anchorEl, setAnchorEl] = useState(null);
    const [showPassword, setShowPassword] = useState(false);
    const [username, setUsername] = useState("");
    const [password, setPassword] = useState("");
    const [challengeState, setChallengeState] = useState(null);
    const [error, setError] = useState("");
    const open = Boolean(anchorEl);
    const [login, {isLoading}] = useLoginMutation();
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const handleOpen = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
        setUsername("");
        setPassword("");
        setError("");
    };

    const handleMobileDialogClose = () => {
        setUsername("");
        setPassword("");
        setError("");
        onClosePopup();
    };

    const handleLogin = async (e) => {
        e.preventDefault();
        setError("");
        toast.promise(
            async () => {
                const result = await login({ username, password }).unwrap();

                if (result.challenge === "NEW_PASSWORD_REQUIRED") {
                    setChallengeState({ username, session: result.session });
                    return;
                }
                const decodedUser = decodeIdToken(result.idToken);
                dispatch(setCredentials({ accessToken: result.accessToken, idToken: result.idToken }));
                handleClose()
                onClosePopup();
                navigate(getRouteForUser(decodedUser), { replace: true });
            },
            {
                loading: t("login.loginLoading"),
                success: t("login.loginSuccess"),
                error: (err) => {
                    setError(err?.data?.message[i18n.language] || t("login.loginFailed"));
                    return err?.data?.message[i18n.language] || t("login.loginFailed");
                }
            }
        )
    };

    const loginForm = (
        <Box
            component="form"
            onSubmit={handleLogin}
            sx={{ display: "flex", flexDirection: "column", gap: 2, }}
        >
            {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                </Alert>
            )}
            <TextField 
                label={t("login.email")} 
                type="email" 
                size="small" 
                fullWidth 
                value={username} 
                onChange={(e) => setUsername(e.target.value)} 
                required 
                sx={{ fontSize: "0.5rem", }} 
            />
            <TextField 
                label={t("login.password")} 
                type={showPassword ? "text" : "password"} 
                size="small" 
                fullWidth 
                value={password} 
                onChange={(e) => setPassword(e.target.value)} 
                required 
                InputProps={{ 
                    endAdornment: (
                        <InputAdornment position="end"> 
                            <IconButton size="small" onClick={() => setShowPassword((prev) => !prev)} edge="end" > 
                                {showPassword ? (<VisibilityOffIcon fontSize="small" />) : (<VisibilityIcon fontSize="small" />)} 
                            </IconButton> 
                        </InputAdornment>
                    ), 
                }} 
            />
            <Button type="submit" variant="contained" fullWidth sx={{ mt: 1, textTransform: "none", borderRadius: 1.5, py: 1, }} > 
                {isLoading ? <CircularProgress size={24} color="inherit" /> : t("login.submit")} 
            </Button>
            <Button variant="text" size="small" sx={{ textTransform: "none", }} > {t("login.forgotPassword")} </Button>
        </Box>
    );

    return (
        <>
            {viewType === "desktop" ? (
                <>
                <Button onClick={handleOpen} sx={{ color: "inherit", textTransform: "none", fontSize: "16px", minWidth: "auto", }} > {t("login.title")} </Button>
                <Popover open={open} anchorEl={anchorEl} onClose={handleClose} anchorOrigin={{ vertical: "bottom", horizontal: "right", }} transformOrigin={{ vertical: "top", horizontal: "right", }} slotProps={{ paper: { sx: { mt: 1, width: 340, borderRadius: 2, boxShadow: "0 8px 30px rgba(0,0,0,0.15)", overflow: "hidden", }, }, }} >
                    <Box sx={{ p: 3 }}>
                        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2, }} >
                            <Typography variant="h6" sx={{ fontWeight: 600, }} > {t("login.title")} </Typography>
                            <IconButton size="small" onClick={handleClose} > <CloseIcon fontSize="small" /> </IconButton>
                        </Box>
                        <Divider sx={{ mb: 2.5 }} />
                        {loginForm}
                    </Box>
                </Popover>
                </>
            ) : (
                <Dialog open={openPopup} onClose={handleMobileDialogClose} fullWidth maxWidth="xs" PaperProps={{ sx: { borderRadius: 2, m: 2, }, }} >
                    <DialogContent sx={{ p: 3 }}>
                        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2, }} >
                            <Typography variant="h6" sx={{ fontWeight: 600, }} > {t("login.title")} </Typography>
                            <IconButton size="small" onClick={handleMobileDialogClose} > <CloseIcon fontSize="small" /> </IconButton>
                        </Box>
                        <Divider sx={{ mb: 2.5 }} />
                        {loginForm}
                    </DialogContent>
                </Dialog>
            )}
            {challengeState && (
                <NewPasswordDialog
                    open={Boolean(challengeState)}
                    username={challengeState.username}
                    session={challengeState.session}
                    onClose={() => setChallengeState(null)}
                    onSuccess={() => {
                        setChallengeState(null);
                        onClosePopup();
                    }}
                />
            )}
        </>
    );
};

export default LoginPopup;